import { KeyValue } from '@angular/common';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, map, Observable, tap, throwError, firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { AFAddressHistoryDomain, AFAuthorization, AFEntity, AFNavigation, AFPersonalInformation, AFRegistrationDomain, SignInModel, AFSummaryOfRights, AFIntroduction, AFDisputeQuestionPojo, AFLocationConfirmation } from '../model/af.model';
import moment from 'moment';
import * as bcrypt from 'bcryptjs';
import { FormGroup } from '@angular/forms';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class AuthService {


  constructor(private readonly http: HttpClient, private readonly router: Router) {
  }

  getIntroduction(): Observable<AFIntroduction> {
    return this.http.post<AFIntroduction>(environment.api_uri + 'getIntroduction', { 'verifyId': this.getVerifyId() });
  }

  getApplicantDetails(isNhp: boolean, isAF: boolean): Observable<AFEntity> {
    return this.http.post<AFEntity>(environment.api_uri + 'getApplicant', { 'verifyId': this.getVerifyId(), 'isNhp': isNhp, 'isAF': isAF });
  }

  getLoginApplicantDetails(isNhp: boolean, isAF: boolean): Observable<AFEntity> {
    return this.http.post<AFEntity>(environment.api_uri + 'getLoginApplicantDetails', { 'verifyId': this.getVerifyId(), 'isNhp': isNhp, 'isAF': isAF });
  }

  getLoginInfo(isNhp: boolean, isAF: boolean): Observable<AFEntity> {
    return this.http.post<AFEntity>(environment.api_uri + 'getLoginInfo', { 'verifyId': this.getVerifyId(), 'isNhp': isNhp, 'isAF': isAF });
  }

  getVendorDetails(): Observable<AFRegistrationDomain> {
    return this.http.post<AFRegistrationDomain>(environment.api_uri + 'registrationVendor', { 'verifyId': this.getVerifyId() });
  }

  getregistrationDetails(): Observable<AFRegistrationDomain> {
    return this.http.post<AFRegistrationDomain>(environment.api_uri + 'registration', { 'verifyId': this.getVerifyId() });
  }

  getPersonalInformation(): Observable<AFPersonalInformation> {
    return this.http.post<AFPersonalInformation>(environment.api_uri + 'getPersonalInformation', { 'verifyId': this.getVerifyId() })
      .pipe(
        map(response => {
          if (response.prevBankFromDate) {
            response.prevBankFromDate = new Date(response.prevBankFromDate);
          }

          if (response.prevBankEndDate) {
            response.prevBankEndDate = new Date(response.prevBankEndDate);
          }

          if (response.pwc != null && response.pwc != undefined) { // zero will be falsy, null and undefined check required
            response.pwc = "" + response.pwc;
          }

          if (response.pwcStartDate) {
            response.pwcStartDate = new Date(response.pwcStartDate);
          }

          if (response.pwcEndDate) {
            response.pwcEndDate = new Date(response.pwcEndDate);
          }

          if (response.nationalIdCountry) {
            response.nationalIdCountry = String(response.nationalIdCountry);
          }

          response.otherNameList.forEach(item => {
            if (item.dateNameChange) {
              item.dateNameChange = new Date(item.dateNameChange);
            }

          });
          return response;
        }));
  }

  getAddressHistory(): Observable<AFAddressHistoryDomain> {
    return this.http.post<AFAddressHistoryDomain>(environment.api_uri + 'getAddressHistory', { 'verifyId': this.getVerifyId() })
      .pipe(
        map(response => {

          response.addressList.forEach(item => {
            if (item.startDate) {
              if (item.startDate) {
                item.startDate = new Date(item.startDate);
              }
              if (item.endDate) {
                item.endDate = new Date(item.endDate);
              }
            }

            if (item.usState) {
              item.usState = item.usState + "";
            }

          });
          return response;
        }));
  }

  getLocationConfirmation(): Observable<AFLocationConfirmation> {
    return this.http.post<AFLocationConfirmation>(environment.api_uri + 'getLocationConfirmation', { 'verifyId': this.getVerifyId() })
      .pipe(
        map(response => {
          if (response.changedAddress?.usState) {
            response.changedAddress.usState = response.changedAddress.usState + "";
          }
          return response;
        }));
  }

  getAuthorizationDetails(): Observable<AFAuthorization> {
    return this.http.post<AFAuthorization>(environment.api_uri + 'getAuthorization', { 'verifyId': this.getVerifyId() });
  }

  getViewAAGeneratedLetter(): Observable<string> {
    return this.http.post<string>(environment.api_uri + 'viewAAGeneratedLetter', { 'tokenId': this.getAaToken() });
  }

  validateNHPToken(token: string, verifyId: string) {
    return this.http.post<string>(environment.api_uri + 'validateNHPToken', { 'token': token, 'verifyId': verifyId });
  }

  getDisputeRegistration(): Observable<string> {
    return this.http.post<string>(environment.api_uri + 'disputeRegistration', { 'verifyId': this.getVerifyId() });
  }

  getDisputeQuestionnaire(): Observable<AFDisputeQuestionPojo> {
    return this.http.post<AFDisputeQuestionPojo>(environment.api_uri + 'getDisputeQuestionnaire', { 'verifyId': this.getVerifyId() });
  }

  saveDisputeQuestionnaire(afDisputeQuestionPojo): Observable<AFDisputeQuestionPojo> {
    const requestUrl = environment.api_uri + 'saveQuestionsAnswers';
    return this.http.post<AFDisputeQuestionPojo>(requestUrl, afDisputeQuestionPojo);
  }

  submitDisputeQuestionnaire(afDisputeQuestionPojo): Observable<string> {
    const requestUrl = environment.api_uri + 'submitQuestionsAnswers';
    return this.http.post<string>(requestUrl, afDisputeQuestionPojo);
  }

  saveAuthorization(afAuthorization): Observable<AFAuthorization> {
    const requestUrl = environment.api_uri + 'saveAuthorization';
    return this.http.post<AFAuthorization>(requestUrl, afAuthorization);
  }


  submitLocationConfirmation(AFLocationConfirmation): Observable<AFLocationConfirmation> {
    const requestUrl = environment.api_uri + 'saveLocationConfirmation';
    return this.http.post<AFLocationConfirmation>(requestUrl, AFLocationConfirmation);

  }

  removeAttachment(attachmentId: string): Observable<string> {
    const requestUrl = environment.api_uri + 'removeAsiAttachment';
    const fd = new FormData();
    fd.append("attachmentId", attachmentId);
    return this.http.post<string>(requestUrl, fd);
  }

  removeMongoAttachment(attachmentId: string): Observable<string> {
    const requestUrl = environment.api_uri + 'removeAttachment';
    const fd = new FormData();
    fd.append("attachmentId", attachmentId);
    return this.http.post<string>(requestUrl, fd);
  }

  removeMongoAttachmentPromise(attachmentId: string) {
    const requestUrl = environment.api_uri + 'removeAttachment';
    const fd = new FormData();
    fd.append("attachmentId", attachmentId);
    return firstValueFrom(this.http.post<string>(requestUrl, fd));
  }

  getSummaryLanguage(): Observable<AFSummaryOfRights> {
    return this.http.post<AFSummaryOfRights>(environment.api_uri + 'summaryOfRights', { 'verifyId': this.getLocalVerifyId() });
  }

  forgotPassword(securityQuestionDomain): Observable<AFRegistrationDomain> {
    const requestUrl = environment.api_uri + 'forgotPassword';
    return this.http.post<AFRegistrationDomain>(requestUrl, securityQuestionDomain);
  }

  forgotPasswordOTP(): Observable<SignInModel> {
    return this.http.post<SignInModel>(environment.api_uri + 'forgotPasswordOTP', { 'verifyId': this.getVerifyId() });
  }

  signIn(SignInModel): Observable<SignInModel> {
    const requestUrl = environment.api_uri + 'signIn';
    return this.http.post<SignInModel>(requestUrl, SignInModel);
  }

  validateOTP(SignInModel): Observable<SignInModel> {
    const requestUrl = environment.api_uri + 'validateOTP';
    return this.http.post<SignInModel>(requestUrl, SignInModel);
  }

  validateSecurityQuestions(securityQuestionDomain): Observable<AFRegistrationDomain> {
    const requestUrl = environment.api_uri + 'validateSecurityQuestions';
    return this.http.post<AFRegistrationDomain>(requestUrl, securityQuestionDomain);
  }

  validateVendorSecurityQuestions(securityQuestionDomain): Observable<AFRegistrationDomain> {
    const requestUrl = environment.api_uri + 'validateVendorSecurityQuestions';
    return this.http.post<AFRegistrationDomain>(requestUrl, securityQuestionDomain);
  }

  resetPassword(resetPasswordDomain): Observable<AFRegistrationDomain> {
    const requestUrl = environment.api_uri + 'resetPassword';
    return this.http.post<AFRegistrationDomain>(requestUrl, resetPasswordDomain);
  }

  resetVendorPassword(resetPasswordDomain): Observable<AFRegistrationDomain> {
    const requestUrl = environment.api_uri + 'resetVendorPassword';
    return this.http.post<AFRegistrationDomain>(requestUrl, resetPasswordDomain);
  }

  createProfile(RegistrationDomain): Observable<AFRegistrationDomain> {
    const requestUrl = environment.api_uri + 'createProfile';
    return this.http.post<AFRegistrationDomain>(requestUrl, RegistrationDomain);
  }

  submitPersonalInformation(AFPersonalInformation: AFPersonalInformation): Observable<AFPersonalInformation> {
    const requestUrl = environment.api_uri + 'savePersonalInformation';

    if (AFPersonalInformation.prevBankFromDate) {
      AFPersonalInformation.prevBankFromDate = moment(AFPersonalInformation.prevBankFromDate).format('MM/DD/YYYY');
    }

    if (AFPersonalInformation.prevBankEndDate) {
      AFPersonalInformation.prevBankEndDate = moment(AFPersonalInformation.prevBankEndDate).format('MM/DD/YYYY');
    }

    if (AFPersonalInformation.pwcStartDate) {
      AFPersonalInformation.pwcStartDate = moment(AFPersonalInformation.pwcStartDate).format('MM/DD/YYYY');
    }

    if (AFPersonalInformation.pwcEndDate) {
      AFPersonalInformation.pwcEndDate = moment(AFPersonalInformation.pwcEndDate).format('MM/DD/YYYY');
    }

    AFPersonalInformation.otherNameList.forEach(item => {
      if (item.dateNameChange) {
        item.dateNameChange = moment(item.dateNameChange).format('MM/DD/YYYY');
      }
    });

    if (AFPersonalInformation.prevBank !== '1') {
      AFPersonalInformation.prevBankFromDate = '';
      AFPersonalInformation.prevBankEndDate = '';
      AFPersonalInformation.personNumber = '';
      AFPersonalInformation.bankAffiliate = '';
    }

    if (AFPersonalInformation.usGreenCard !== '1') {
      AFPersonalInformation.usGreenCardDocumentNumber = '';
      AFPersonalInformation.usGreenCardIssueMonth = '';
      AFPersonalInformation.usGreenCardIssueDay = '';
      AFPersonalInformation.usGreenCardIssueYear = '';
      AFPersonalInformation.usGreenCardExpiryMonth = '';
      AFPersonalInformation.usGreenCardExpiryDay = '';
      AFPersonalInformation.usGreenCardExpiryYear = '';
      AFPersonalInformation.usGreenCardSSN = '';
    }

    if (AFPersonalInformation.confirmSsn) {
      let salt = bcrypt.genSaltSync();
      AFPersonalInformation.confirmSsn = bcrypt.hashSync(AFPersonalInformation.confirmSsn, salt);
    }

    return this.http.post<AFPersonalInformation>(requestUrl, AFPersonalInformation);
  }

  submitAddressHistory(AFAddressHistoryDomain): Observable<AFAddressHistoryDomain> {
    const requestUrl = environment.api_uri + 'saveAddress';
    return this.http.post<AFAddressHistoryDomain>(requestUrl, AFAddressHistoryDomain);
  }


  updatePassword(SignInModel): Observable<SignInModel> {
    const requestUrl = environment.api_uri + 'updatePassword';
    return this.http.post<SignInModel>(requestUrl, SignInModel);
  }

  updateVendorPassword(SignInModel): Observable<SignInModel> {
    const requestUrl = environment.api_uri + 'updateVendorPassword';
    return this.http.post<SignInModel>(requestUrl, SignInModel);
  }

  isAuthenticated(): boolean {
    return (sessionStorage.getItem('verifyId') != null);
  }

  hasValidatedReset(): boolean {
    return (sessionStorage.getItem('maxCredential') != null);
  }

  validNavigationalUrl(navUrl: string): boolean {
    let userNavItems: AFNavigation[] = this.getNavLinks();
    let maxNavigationValue: string = sessionStorage.getItem('maxNavigation');
    let returnVal: boolean = true;
    if (userNavItems) {
      for (let entry of userNavItems) {
        if (entry.routerLink == '/' + navUrl) {
          if (Number(maxNavigationValue) < entry.id) {
            returnVal = false;
          }
          break;
        }
      }
    }
    return returnVal;
  }

  getVerifyId(): string {
    return (sessionStorage.getItem('verifyId'));
  }

  getLocalVerifyId(): string { //session variables are tied to browser tabs
    return (localStorage.getItem('verifyIdLocal'));
  }

  getAaToken(): string {
    return (sessionStorage.getItem('aaToken'));
  }

  getBookmarkUrl(): string {
    const user = sessionStorage.getItem(environment.user_object);
    if (user) {
      const u: AFEntity = JSON.parse(user);
      return u.bookmarkUrl;
    }
    return null;
  }

  getUser(): AFEntity {
    const user = sessionStorage.getItem(environment.user_object);
    if (user) {
      const u: AFEntity = JSON.parse(user);
      return u;
    }
    return null;
  }

  getNavLinks(): AFNavigation[] {

    const user = sessionStorage.getItem(environment.user_object);
    if (user) {
      const u: AFEntity = JSON.parse(user);
      let showCpa = sessionStorage.getItem('show_cpa');
      if (this.getJobState() && this.getJobState().toUpperCase() == 'CA' || showCpa == "true") {
        return u.navList;
      }

      if (this.getJobCountry() === 'IND' && this.getJobSubCountry() === 'GBS') {
        const item = u.navList.find(nav => nav.name === 'Fitness and Propriety Questions');
        if (item) {
          item.name = 'Criminal History';
        }
      }

      return u.navList.filter((value) => {
        return (value.routerLink != "/cpa");
      });
    }
    return null;
  }

  getAfId(): string {
    const user = sessionStorage.getItem(environment.user_object);
    if (user) {
      const u: AFEntity = JSON.parse(user);
      return u.afId;
    }
    return null;
  }

  getJobState(): string {
    const user = sessionStorage.getItem(environment.user_object);
    if (user) {
      const u: AFEntity = JSON.parse(user);
      return u.jobState;
    }
    return null;
  }

  getJobCountry(): string {
    const user = sessionStorage.getItem(environment.user_object);
    if (user) {
      const u: AFEntity = JSON.parse(user);
      return u.jobCountry;
    }
    return null;
  }

  getJobSubCountry(): string {
    const user = sessionStorage.getItem(environment.user_object);
    if (user) {
      const u: AFEntity = JSON.parse(user);
      if (u.countryRules) {
        return u.countryRules.jobSubCountry;
      }
    }
    return null;
  }

  getJobCountryValue(): string {
    const user = sessionStorage.getItem(environment.user_object);
    if (user) {
      const u: AFEntity = JSON.parse(user);
      if (u.countryRules) {
        return u.countryRules.jobCountryValue;
      }

    }
    return null;
  }

  getAddressLimit(): number {
    const user = sessionStorage.getItem(environment.user_object);
    if (user) {
      const u: AFEntity = JSON.parse(user);
      if (u.countryRules) {
        return u.countryRules.addressLimit;
      }
    }
    return null;
  }

  getJobCountryOnboardingList(): boolean {
    // add new country to onboard as part of OUS list 
    // then it will return ture to action accordingly
    const listOfOUS = ['AU', 'IRL'];

    let isOUSList = false;
    let currentJobCountry = this.getJobCountry();
    listOfOUS.forEach((country) => {
      if (currentJobCountry == country) {
        isOUSList = true;
      }
    });

    return isOUSList;
  }


  getVendorUser(): AFRegistrationDomain {
    const user = sessionStorage.getItem(environment.user_vendor_object);
    if (user) {
      const u: AFRegistrationDomain = JSON.parse(user);
      return u;
    }
    return null;
  }

  isSwapsAP(): boolean {
    const user = sessionStorage.getItem(environment.user_object);
    if (user) {
      const u: AFEntity = JSON.parse(user);
      return u.ukBasicDisclosure;
    }
    return null;
  }

  getOtherNameText(): string {
    const user = sessionStorage.getItem(environment.user_object);
    if (user) {
      const u: AFEntity = JSON.parse(user);
      if (u.countryRules) {
        return u.countryRules.otherNameText;
      }
    }
    return null;
  }

  getEmploymentGapInstruction(): number {
    const user = sessionStorage.getItem(environment.user_object);
    if (user) {
      const u: AFEntity = JSON.parse(user);
      if (u.countryRules) {
        return u.countryRules.employmentGapInstruction;
      }
    }
    return null;
  }

  getAddressHistoryInstruction(): number {
    const user = sessionStorage.getItem(environment.user_object);
    if (user) {
      const u: AFEntity = JSON.parse(user);
      if (u.countryRules) {
        return u.countryRules.addressHistoryInstruction;
      }
    }
    return null;
  }

  getOutsideJobCountryDocument(): number {
    const user = sessionStorage.getItem(environment.user_object);
    if (user) {
      const u: AFEntity = JSON.parse(user);
      if (u.countryRules) {
        return u.countryRules.outsideJobCountryDocument;
      }
    }
    return null;
  }

  getRequireOutsideJobCountryDocument(): string {
    const user = sessionStorage.getItem(environment.user_object);
    if (user) {
      const u: AFEntity = JSON.parse(user);
      if (u.countryRules?.requiredDocumentJobCountry) {
        return u.countryRules.requiredDocumentJobCountry;
      }
    }
    return null;
  }

  getEducationHistoryInstruction(): string {
    const user = sessionStorage.getItem(environment.user_object);
    if (user) {
      const u: AFEntity = JSON.parse(user);
      if (u.countryRules) {
        return u.countryRules.educationHistoryInstruction;
      }
    }
    return null;
  }

  getLicenseIdText(): string {
    const user = sessionStorage.getItem(environment.user_object);
    if (user) {
      const u: AFEntity = JSON.parse(user);
      if (u.countryRules) {
        return u.countryRules.licenseIdText;
      }
    }
    return null;
  }

  createVendorProfile(RegistrationDomain): Observable<AFRegistrationDomain> {
    const requestUrl = environment.api_uri + 'createVendorProfile';
    return this.http.post<AFRegistrationDomain>(requestUrl, RegistrationDomain);
  }

  vendorSignIn(SignInModel): Observable<SignInModel> {
    const requestUrl = environment.api_uri + 'vendorSignIn';
    return this.http.post<SignInModel>(requestUrl, SignInModel);
  }

  generatePDF(afId: string, verifyId: string, fileName: string): Observable<any> {
    let params = new HttpParams().set("afId", afId).set("verifyId", verifyId);
    let headers = new HttpHeaders({
      "Accept": "application/pdf"
    });

    return this.http.post(environment.api_uri + "generatePDF", params, { headers: headers, responseType: "blob" }).pipe(
      tap((response: Blob) => {
        const blob = new Blob([response], {
          type: "application/pdf"
        });

        // A real file extension is required so every browser (including current
        // Safari/WebKit) treats this as a download instead of navigating the tab
        // to its built-in inline PDF viewer, which would tear down the SPA.
        const downloadName = fileName.toLowerCase().endsWith(".pdf") ? fileName : `${fileName}.pdf`;

        // Trigger a same-page download via an anchor with the download attribute.
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = downloadName;
        link.style.display = "none";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Safari/WebKit keeps reading from the blob's object URL until the file
        // finishes saving. Revoking it too early aborts that read with
        // "WebKitBlobResource error 1", so defer the revoke well past the download
        // hand-off (FileSaver.js uses the same 40s guard).
        setTimeout(() => window.URL.revokeObjectURL(url), 40000);
      }),
      catchError(error => {
        console.log("Error downloading file: ", error);
        return throwError(() => error);
      }));
  }

  downloadLADocument(fileName: string): Observable<any> {
    let headers = new HttpHeaders({
      "Accept": "application/pdf"
    });

    return this.http.post(environment.api_uri + "downloadLADocument", new HttpParams(), { headers: headers, responseType: "blob" }).pipe(
      tap((response: Blob) => {
        const blob = new Blob([response], {
          type: "application/pdf"
        });

        // A real file extension is required so every browser (including current
        // Safari/WebKit) treats this as a download instead of navigating the tab
        // to its built-in inline PDF viewer, which would tear down the SPA.
        const downloadName = fileName.toLowerCase().endsWith(".pdf") ? fileName : `${fileName}.pdf`;

        // Trigger a same-page download via an anchor with the download attribute.
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = downloadName;
        link.style.display = "none";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Safari/WebKit keeps reading from the blob's object URL until the file
        // finishes saving. Revoking it too early aborts that read with
        // "WebKitBlobResource error 1", so defer the revoke well past the download
        // hand-off (FileSaver.js uses the same 40s guard).
        setTimeout(() => window.URL.revokeObjectURL(url), 40000);
      }),
      catchError(error => {
        console.log("Error downloading file: ", error);
        return throwError(() => error);
      }));
  }

  downloadAcknowledgmentPDF(fileName: string, jobCountry: string, jobSubCountry: string, afId: string): Observable<any> {
    let headers = new HttpHeaders({
      "Accept": "application/pdf"
    });

    let params = new HttpParams().set("jobCountry", jobCountry).set("jobSubCountry", jobSubCountry).set("afId", afId);

    return this.http.post(environment.api_uri + "downloadAcknowledgmentPDF", params, { headers: headers, responseType: "blob" }).pipe(
      tap((response: Blob) => {
        // Completion of this step depends on the PDF being successfully served
        // by the request above, not on the browser's download side effect. On
        // Safari/WebKit the anchor-based download can throw (e.g.
        // "WebKitBlobResource error 1") or navigate the tab to the inline PDF
        // viewer. Isolate the browser side effect in a try/catch so any such
        // failure cannot turn a successful fetch into an observable error and
        // leave the acknowledgement step marked incomplete.
        try {
          const blob = new Blob([response], {
            type: "application/pdf"
          });

          // A real file extension is required so every browser (including current
          // Safari/WebKit) treats this as a download instead of navigating the tab
          // to its built-in inline PDF viewer, which would tear down the SPA.
          const downloadName = fileName.toLowerCase().endsWith(".pdf") ? fileName : `${fileName}.pdf`;

          // Trigger a same-page download via an anchor with the download attribute.
          // No target/window.open is used, so the user stays on the acknowledgement page.
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = downloadName;
          link.style.display = "none";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          // Safari/WebKit keeps reading from the blob's object URL until the file
          // finishes saving. Revoking it too early aborts that read with
          // "WebKitBlobResource error 1", so defer the revoke well past the download
          // hand-off (FileSaver.js uses the same 40s guard).
          setTimeout(() => window.URL.revokeObjectURL(url), 40000);
        } catch (downloadError) {
          console.log("Error triggering PDF download side effect: ", downloadError);
        }
      }),
      catchError(error => {
        console.log("Error downloading file: ", error);
        return throwError(() => error);
      }));
  }

  downloadOusFinraConsentPDF(fileName: string, jobCountry: string, jobSubCountry: string, afId: string): Observable<any> {
    let headers = new HttpHeaders({
      "Accept": "application/pdf"
    });

    let data = {
      "jobCountry": jobCountry,
      "jobSubCountry": jobSubCountry,
      "afId": afId
    }

    return this.http.post(environment.api_uri + "downloadOusFinraConsent", data, { headers: headers, responseType: "blob" }).pipe(
      tap((response: Blob) => {
        const blob = new Blob([response], {
          type: "application/pdf"
        });

        // A real file extension is required so every browser (including current
        // Safari/WebKit) treats this as a download instead of navigating the tab
        // to its built-in inline PDF viewer, which would tear down the SPA.
        const downloadName = fileName.toLowerCase().endsWith(".pdf") ? fileName : `${fileName}.pdf`;

        // Trigger a same-page download via an anchor with the download attribute.
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = downloadName;
        link.style.display = "none";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Safari/WebKit keeps reading from the blob's object URL until the file
        // finishes saving. Revoking it too early aborts that read with
        // "WebKitBlobResource error 1", so defer the revoke well past the download
        // hand-off (FileSaver.js uses the same 40s guard).
        setTimeout(() => window.URL.revokeObjectURL(url), 40000);
      }),
      catchError(error => {
        console.log("Error downloading file: ", error);
        return throwError(() => error);
      }));
  }

  downloadAttachment(attachmentId: string, fileName: string): Observable<any> {

    const safeFileName = fileName ?? "";
    const fileExtension = safeFileName.split('.').pop()?.toLowerCase();

    // Map of supported file extensions to their MIME types
    const mimeTypes: { [key: string]: string } = {
      txt: "text/plain",
      doc: "application/msword",
      docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      eml: "message/rfc822",
      msg: "application/vnd.ms-outlook",
      xls: "application/vnd.ms-excel",
      xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      pdf: "application/pdf",
      ppt: "application/vnd.ms-powerpoint",
      pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
      jpg: "image/jpeg",
      jpeg: "image/jpeg",
      bmp: "image/bmp",
      tif: "image/tiff",
      tiff: "image/tiff",
      png: "image/png"
    };

    // Get the MIME type for the file extension, default to "application/octet-stream" if unknown
    const mimeType = mimeTypes[fileExtension] || "application/octet-stream";

    let headers = new HttpHeaders({
      "Accept": mimeType
    });

    let params = new HttpParams().set("attachmentId", attachmentId);

    return this.http.post(environment.api_uri + "downloadAttachment", params, { headers: headers, responseType: "blob" }).pipe(
      tap((response: Blob) => {
        const blob = new Blob([response], {
          type: mimeType
        });

        // The caller-provided fileName already includes the real extension, so the
        // download attribute is honored by every browser (including current Safari).
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = fileName;
        link.style.display = "none";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Safari/WebKit keeps reading from the blob's object URL until the file
        // finishes saving. Revoking it too early aborts that read with
        // "WebKitBlobResource error 1", so defer the revoke well past the download
        // hand-off (FileSaver.js uses the same 40s guard).
        setTimeout(() => window.URL.revokeObjectURL(url), 40000);
      }),
      catchError(error => {
        console.log("Error downloading file: ", error);
        return throwError(() => error);
      }));
  }

  setFocus(form: FormGroup) {
    if (form.invalid) {
      const firstInvalidField = Object.keys(form.controls).find(
        field => form.get(field)?.invalid
      );

      // Try to find the element directly or as a descendant, updated code required to find textarea
      let element: HTMLElement | null = document.querySelector(
        `[formControlName="${firstInvalidField}"], [formControlName="${firstInvalidField}"] input, [formControlName="${firstInvalidField}"] textarea, [formControlName="${firstInvalidField}"] select`
      );

      if (!element) {
        const panels = document.querySelectorAll('mat-expansion-panel[aria-expanded="true"]');
        panels.forEach(panel => {
          if (!element) {
            element = panel.querySelector(
              `[formControlName="${firstInvalidField}"], [formControlName="${firstInvalidField}"] input, [formControlName="${firstInvalidField}"] textarea, [formControlName="${firstInvalidField}"] select`
            );
          }
        });
      }

      if (element) {
        element?.focus();
      }

      setTimeout(() => {
        window.scrollTo(0, window.scrollY);
      }, 100);
    }
  }

  refreshToken(): Observable<{ accessToken: string, expiresAt: string }> {
    // Prepare the request parameters
    let params = new HttpParams();
    params = params.set("verifyId", this.getVerifyId()); // Add the "standardId" parameter to the request

    // Make the HTTP POST request to the "refresh" endpoint
    return this.http.post<{ accessToken: string, expiresAt: string }>(
      environment.api_uri + "refresh", // API endpoint for refreshing the token
      params, // Request parameters
      { withCredentials: true } // Ensure cookies (e.g., session cookies) are sent with the request
    ).pipe(
      // Handle the response from the API
      tap((response) => {
        // Extract and store the new access token in sessionStorage
        const newToken = "applicant-form " + response.accessToken; // Prefix the token with "applicant-form "
        if (newToken) {
          sessionStorage.setItem(environment.x_auth_token_header, newToken); // Store the new token
        }

        // Update the session expiration time in localStorage
        if (response.expiresAt) {
          localStorage.setItem('sessionExpiration', response.expiresAt); // Store the new expiration time
        }
      }),
      // Map the response to return only the accessToken and expiresAt values
      map((response) => {
        return {
          accessToken: response.accessToken,
          expiresAt: response.expiresAt
        };
      }),
      // Handle any errors that occur during the API call
      catchError(() => {
        return throwError(() => new Error('Failed to refresh token')); // Throw an error observable
      })
    );
  }

  logout(): void {
    this.router.navigate(['sessionTimedOut']);
  }

  invalidateTokenFamily(): Observable<any> {
    return this.http.post(environment.api_uri + 'logout', { verifyId: this.getVerifyId() }, { responseType: "text" });
  }
}

export function valueAscOrder(a: KeyValue<number, string>, b: KeyValue<number, string>): number {
  return a.value.localeCompare(b.value);
}
